import React from "react";
import { Link } from "react-router-dom";
import { Scale, ArrowLeft, FileText, AlertTriangle, Ban, ShieldCheck, Coins, MessageCircle, Sparkles } from "lucide-react";

const LAST_UPDATE = "27 avril 2026";

const Article = ({ num, title, icon: Icon, id, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="flex items-baseline gap-3 mb-3">
      <span className="font-display text-2xl font-black text-brand-orange shrink-0">{num}.</span>
      <h2 className="font-display text-2xl md:text-3xl font-black flex items-center gap-2">
        {Icon && <Icon size={22} className="text-white/60" />}
        {title}
      </h2>
    </div>
    <div className="text-white/75 leading-relaxed space-y-3 text-[15px] pl-9">
      {children}
    </div>
  </section>
);

export const TermsPage = () => (
  <div className="px-4 md:px-12 py-10 max-w-4xl mx-auto pb-32" data-testid="terms-page">
    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-6">
      <ArrowLeft size={14} /> Retour à l'accueil
    </Link>

    <header className="mb-10">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-xs font-bold uppercase tracking-widest mb-4">
        <Scale size={11} /> CGU
      </span>
      <h1 className="font-display text-4xl md:text-6xl font-black mb-3 tracking-tight">
        Conditions Générales d'Utilisation
      </h1>
      <p className="text-white/60">
        Bienvenue sur Voamada. En utilisant notre plateforme, tu acceptes les règles ci-dessous.
        Lis-les attentivement — elles existent pour protéger les artistes, les fans et la communauté.
      </p>
      <p className="text-xs text-white/40 mt-3">Dernière mise à jour : {LAST_UPDATE}</p>
    </header>

    <div className="space-y-10">
      <Article num="1" title="Objet" icon={FileText} id="objet">
        <p>Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'usage du site
        <strong> voamada.com</strong> (ci-après "le Service"), édité par le projet Voamada. Le Service est une plateforme
        de découverte et de promotion de talents malgaches et africains : musique, humour, cinéma, danse,
        art visuel, mode et cuisine.</p>
        <p>L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU. Si tu n'acceptes pas
        ces conditions, tu ne dois pas utiliser le Service.</p>
      </Article>

      <Article num="2" title="Création de compte" icon={ShieldCheck} id="compte">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>L'inscription est gratuite et ouverte à toute personne âgée d'au moins <strong>13 ans</strong> (avec accord parental jusqu'à 16 ans)</li>
          <li>Tu fournis des informations exactes et à jour, et tu es responsable de la confidentialité de ton mot de passe</li>
          <li>Un seul compte par personne — la création de plusieurs comptes pour fausser le système (votes, rewards Vola) est interdite</li>
          <li>Voamada se réserve le droit de suspendre ou supprimer tout compte en cas de manquement aux CGU</li>
        </ul>
      </Article>

      <Article num="3" title="Contenus publiés par les utilisateurs" icon={Sparkles} id="contenus">
        <p><strong>3.1 Tu restes propriétaire</strong> de tes contenus (musique, vidéos, photos, textes). Voamada ne revendique
        aucun droit sur tes œuvres.</p>
        <p><strong>3.2 Licence accordée à Voamada :</strong> en publiant un contenu, tu nous accordes une licence non-exclusive,
        gratuite, mondiale et limitée à la durée de présence du contenu sur le Service, pour le diffuser, le reproduire,
        l'afficher et le promouvoir sur la plateforme et ses canaux de communication associés (réseaux sociaux officiels Voamada,
        newsletters, partenariats événementiels).</p>
        <p><strong>3.3 Garanties :</strong> tu certifies être titulaire de tous les droits nécessaires (droits d'auteur,
        droits voisins, droits à l'image) sur les contenus que tu publies, ou avoir obtenu les autorisations requises
        des ayants droit concernés.</p>
        <p><strong>3.4 Suppression :</strong> tu peux supprimer tes contenus à tout moment depuis ton dashboard.
        Les sauvegardes peuvent les conserver jusqu'à 30 jours après suppression.</p>
      </Article>

      <Article num="4" title="Contenus interdits" icon={Ban} id="interdits">
        <p>Sont strictement interdits sur Voamada :</p>
        <ul className="list-disc pl-5 space-y-1.5 mt-2">
          <li>Contenus violant les droits d'auteur ou la propriété intellectuelle de tiers</li>
          <li>Contenus à caractère pornographique, pédopornographique ou sexuel explicite</li>
          <li>Discours de haine, racisme, incitation à la violence, harcèlement</li>
          <li>Apologie du terrorisme, contenus illégaux ou dangereux</li>
          <li>Spam, scams, schémas pyramidaux, faux profils</li>
          <li>Contenus diffamatoires ou portant atteinte à la vie privée d'autrui</li>
          <li>Tentatives de piratage, scraping massif, attaques contre l'infrastructure</li>
        </ul>
        <p className="mt-3">Voamada se réserve le droit de retirer tout contenu signalé ou jugé non conforme,
        sans préavis ni indemnité.</p>
      </Article>

      <Article num="5" title="Modération & signalement" icon={MessageCircle} id="moderation">
        <p>Tout utilisateur peut signaler un contenu inapproprié via le bouton "Signaler" présent sur chaque page.
        Notre équipe examine les signalements dans un délai maximum de <strong>72 heures</strong>.</p>
        <p>Pour les contenus illicites manifestes (LCEN — France), le signalement peut être fait directement à
        <a className="text-brand-orange hover:underline ml-1" href="mailto:abuse@voamada.com">abuse@voamada.com</a>.</p>
      </Article>

      <Article num="6" title="Programme Voamada Vola" icon={Coins} id="vola">
        <p><strong>6.1 Nature :</strong> les "Vola" sont des points de fidélité virtuels, sans valeur monétaire en eux-mêmes.
        Ils peuvent être convertis en récompenses (badges, Premium, crédit Mvola, merch) selon le catalogue en vigueur.</p>
        <p><strong>6.2 Cumul :</strong> les Vola sont gagnés uniquement par des actions légitimes (connexion quotidienne,
        likes, commentaires, parrainage). Toute manipulation (faux comptes, bots, achats artificiels) entraîne
        l'annulation des points et la suspension du compte.</p>
        <p><strong>6.3 Conversion en cash :</strong> certaines récompenses Mvola (en Ariary) sont expédiées sous 48h ouvrées
        après vérification du numéro fourni. Voamada se réserve le droit de refuser une conversion en cas de soupçon de fraude.</p>
        <p><strong>6.4 Expiration :</strong> les Vola expirent après <strong>12 mois d'inactivité</strong> du compte.</p>
        <p><strong>6.5 Modifications :</strong> les taux de conversion, le catalogue et les règles peuvent être modifiés
        à tout moment. Les changements sont annoncés par email aux utilisateurs concernés.</p>
      </Article>

      <Article num="7" title="Abonnement Premium" id="premium">
        <p>L'abonnement Premium offre des fonctionnalités avancées (sans pub, qualité supérieure, accès anticipé).
        Le tarif et les modalités sont indiqués sur la page <Link to="/premium" className="text-brand-orange hover:underline">/premium</Link>.</p>
        <p>L'abonnement est résiliable à tout moment depuis le dashboard. Aucun remboursement au prorata
        n'est effectué pour la période en cours, mais l'accès aux fonctionnalités Premium reste actif jusqu'à la
        fin de la période payée.</p>
      </Article>

      <Article num="8" title="Publicité & sponsors" id="publicite">
        <p>Voamada finance le service via :</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Google AdSense (régie publicitaire automatique — voir <Link to="/confidentialite#adsense" className="text-brand-orange hover:underline">Politique de confidentialité §7</Link>)</li>
          <li>Sponsors directs sélectionnés par l'équipe Voamada</li>
          <li>Sondages sponsorisés (réponse volontaire récompensée en Vola)</li>
          <li>Abonnements Premium</li>
        </ul>
        <p className="mt-3">Tout annonceur souhaitant collaborer peut nous contacter via <Link to="/annoncer" className="text-brand-orange hover:underline">/annoncer</Link>.</p>
      </Article>

      <Article num="9" title="Responsabilité" icon={AlertTriangle} id="responsabilite">
        <p><strong>9.1</strong> Voamada s'efforce d'assurer la disponibilité du Service 24/7 mais ne peut garantir
        l'absence d'interruption (maintenance, panne d'hébergeur, force majeure).</p>
        <p><strong>9.2</strong> Voamada ne peut être tenu responsable des contenus publiés par les utilisateurs.
        Chaque auteur est seul responsable de ses publications.</p>
        <p><strong>9.3</strong> Voamada ne peut être tenu responsable des dommages indirects (perte de données,
        manque à gagner, atteinte à l'image) résultant de l'utilisation du Service, dans les limites permises par la loi.</p>
      </Article>

      <Article num="10" title="Propriété intellectuelle Voamada" id="ip">
        <p>La marque <strong>Voamada</strong>, le logo, l'identité visuelle, le code source et les éléments graphiques
        de la plateforme sont la propriété exclusive du projet Voamada et protégés par le droit de la propriété
        intellectuelle. Toute reproduction non autorisée est interdite.</p>
      </Article>

      <Article num="11" title="Modifications des CGU" id="modifications">
        <p>Voamada se réserve le droit de modifier les présentes CGU à tout moment. Les modifications substantielles
        seront notifiées par email et/ou par une bannière sur le site au moins 15 jours avant leur entrée en vigueur.
        En continuant à utiliser le Service après cette date, tu acceptes les nouvelles CGU.</p>
      </Article>

      <Article num="12" title="Loi applicable & juridiction" id="droit">
        <p>Les présentes CGU sont régies par le <strong>droit français</strong> et, à titre subsidiaire, par le droit
        malgache pour les utilisateurs résidant à Madagascar. Tout litige sera soumis à une tentative de conciliation
        amiable. À défaut, les tribunaux compétents seront ceux du ressort du siège de l'éditeur (France).</p>
        <p className="text-sm text-white/55 mt-3">Les consommateurs au sens du droit européen conservent le bénéfice des
        dispositions impératives de la loi de leur pays de résidence.</p>
      </Article>

      <Article num="13" title="Contact" id="contact">
        <p>Pour toute question relative aux CGU :</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Email général : <a className="text-brand-orange hover:underline" href="mailto:contact@voamada.com">contact@voamada.com</a></li>
          <li>Signalement abus : <a className="text-brand-orange hover:underline" href="mailto:abuse@voamada.com">abuse@voamada.com</a></li>
        </ul>
        <p className="mt-3 text-sm text-white/60">
          Voir aussi : <Link to="/confidentialite" className="text-brand-orange hover:underline">Politique de confidentialité</Link> ·
          <Link to="/cookies" className="text-brand-orange hover:underline ml-1">Cookies</Link> ·
          <Link to="/mentions-legales" className="text-brand-orange hover:underline ml-1">Mentions légales</Link>
        </p>
      </Article>
    </div>
  </div>
);

export default TermsPage;
